import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { TypeAnimation } from 'react-type-animation';
import { ChevronDown, ChevronUp, DollarSign, AlertCircle, RefreshCw } from 'lucide-react';
import { Message, MessageMetadata } from '../types';
import { parseXmlToHtml, isXmlContent } from './xml-parser';
import { ConfidenceBadge } from './confidence-badge';
import { ContextUsageBadge } from './context-usage-badge';
import { WorkflowProgress } from './workflow-progress';
import { KeyInsightsCard } from './key-insights-card';
import { RecommendedActionsCard } from './recommended-actions-card';

interface ChatMessageProps {
  message: Message | ChatFetchedMessage;
  animate?: boolean;
  onRetry?: () => void;
}

interface ChatFetchedMessage extends Message {
  timestamp: Date;
  type: 'human' | 'ai';
}

function formatCellValue(value: any): string {
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(2);
  }
  return String(value ?? '');
}

function flattenDetails(details: Record<string, any>[]): Record<string, any>[] {
  if (!details.length) return details;

  const hasItems = details.some(d => Array.isArray(d.items));
  if (!hasItems) return details;

  const flattened: Record<string, any>[] = [];
  for (const group of details) {
    const { items, ...parentFields } = group;
    if (Array.isArray(items)) {
      for (const item of items) {
        flattened.push({ ...parentFields, ...item });
      }
    } else {
      flattened.push(group);
    }
  }
  return flattened;
}

function DetailsTable({ details: rawDetails }: { details: Record<string, any>[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!rawDetails || rawDetails.length === 0) return null;

  const details = flattenDetails(rawDetails);
  const headers = Object.keys(details[0]);
  const previewRows = details.slice(0, 10);
  const displayRows = isExpanded ? details : previewRows;
  const hasMore = details.length > 10;

  return (
    <div className="mt-3" data-testid="chat-message-details">
      <div className="text-xs font-medium text-gray-500 mb-1">
        {details.length} result{details.length !== 1 ? 's' : ''}
      </div>
      <div className="overflow-x-auto rounded border border-gray-200 max-h-[300px] overflow-y-auto [scrollbar-width:thin]">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {headers.map((header) => (
                <th key={header} className="px-2 py-1.5 text-left font-medium text-gray-600 whitespace-nowrap border-b border-gray-200">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                {headers.map((header) => {
                  const val = row[header];
                  const isNegative = typeof val === 'number' && val < 0;
                  return (
                    <td
                      key={header}
                      className={`px-2 py-1 whitespace-nowrap border-b border-gray-100 ${
                        typeof val === 'number' ? 'text-right tabular-nums' : ''
                      } ${isNegative ? 'text-red-600' : typeof val === 'number' ? 'text-gray-900' : ''}`}
                    >
                      {formatCellValue(val)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
        >
          {isExpanded ? (
            <>Collapse <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>Show all {details.length} rows <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      )}
    </div>
  );
}

function CostTooltip({ metadata }: { metadata: MessageMetadata }) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const usage = metadata.usage;
  if (!usage) return null;

  const fmt = (n: number) => n.toLocaleString();
  const fmtCost = (n: number) => `$${n.toFixed(6)}`;

  const handleOpen = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top - 8,
        left: Math.max(8, rect.right - 288),
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center text-gray-400 hover:text-green-600 transition-colors p-0.5 rounded"
        title="View cost details"
      >
        <DollarSign className="h-3 w-3" />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)} />
          <div
            className="fixed z-[101] w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-xs text-gray-700"
            style={{ top: position.top, left: position.left, transform: 'translateY(-100%)' }}
          >
            <div className="font-semibold text-gray-900 mb-2">Cost & Usage Details</div>

            {metadata.model && (
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Model</span>
                <span className="">{metadata.model}</span>
              </div>
            )}
            {metadata.duration != null && (
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Duration</span>
                <span className="">{(metadata.duration / 1000).toFixed(1)}s</span>
              </div>
            )}
            {metadata.drilldownRows != null && (
              <div className="flex justify-between mb-1">
                <span className="text-gray-500">Rows queried</span>
                <span className="">{fmt(metadata.drilldownRows)}</span>
              </div>
            )}
            {metadata.filteredRows != null && (
              <div className="flex justify-between mb-2">
                <span className="text-gray-500">Rows after filter</span>
                <span className="">{fmt(metadata.filteredRows)}</span>
              </div>
            )}

            <div className="border-t border-gray-100 pt-2 mt-1">
              <div className="font-semibold text-gray-900 mb-1">Intent Parsing (Pass 1)</div>
              <div className="flex justify-between"><span className="text-gray-500">Tokens</span><span className="">{fmt(usage.intentParsing.totalTokens)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Cost</span><span className=" text-green-700">{fmtCost(usage.intentParsing.costUSD)}</span></div>
            </div>

            <div className="border-t border-gray-100 pt-2 mt-2">
              <div className="font-semibold text-gray-900 mb-1">Analysis (Pass 2)</div>
              <div className="flex justify-between"><span className="text-gray-500">Prompt tokens</span><span className="">{fmt(usage.analysis.promptTokens)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Completion tokens</span><span className="">{fmt(usage.analysis.completionTokens)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Cost</span><span className=" text-green-700">{fmtCost(usage.analysis.costUSD)}</span></div>
            </div>

            <div className="border-t border-gray-200 pt-2 mt-2 font-semibold">
              <div className="flex justify-between"><span>Total tokens</span><span className="">{fmt(usage.total.totalTokens)}</span></div>
              <div className="flex justify-between"><span>Total cost</span><span className=" text-green-700">{fmtCost(usage.total.costUSD)}</span></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function ChatMessage({ message, animate = false, onRetry }: ChatMessageProps) {
  const isUser = message.sender === 'user' || (message as ChatFetchedMessage).type === 'human';
  const messageRef = useRef<HTMLDivElement>(null);
  const isError = (message as Message).isError || message.id.startsWith('error_');

  if (isError && !isUser) {
    return (
      <div className="flex justify-start mb-3" data-testid="chat-message-error">
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 max-w-[90%]">
          <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
          <span className="text-xs text-red-700">{message.text}</span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium ml-1"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  const rawContent = message.text || (message as any).data.content;
  const hasXmlContent = !isUser && isXmlContent(rawContent);
  const details = (message as Message).details;

  const getProcessedContent = (text: string): string => {
    if (isUser || !isXmlContent(text)) {
      return text;
    }
    return parseXmlToHtml(text);
  };

  useEffect(() => {
    if (!messageRef.current || isUser) return;

    const handleClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const clickableElement = target.closest('[data-redirecturl]') as HTMLElement;

      if (clickableElement && clickableElement.dataset.redirecturl) {
        event.preventDefault();
        window.open(clickableElement.dataset.redirecturl, '_blank', 'noopener,noreferrer');
      }
    };

    messageRef.current.addEventListener('click', handleClick);

    return () => {
      if (messageRef.current) {
        messageRef.current.removeEventListener('click', handleClick);
      }
    };
  }, [isUser]);

  const shouldAnimate = animate && !isUser && !hasXmlContent;

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
      data-testid={`chat-message-${message.sender}`}
    >
      <div
        ref={messageRef}
        className={`px-4 py-2 rounded-lg ${
          isUser
            ? 'max-w-[80%] bg-blue-600 text-white rounded-br-sm'
            : `bg-gray-100 text-gray-900 rounded-bl-sm ${details && details.length > 0 ? 'max-w-[95%]' : 'max-w-[80%]'}`
        }`}
      >
        {!isUser && (message as Message).workflowNodes && (message as Message).workflowNodes!.length > 0 && (
          <WorkflowProgress nodes={(message as Message).workflowNodes!} />
        )}
        {shouldAnimate ? (
          <TypeAnimation
            sequence={[rawContent]}
            speed={80}
            cursor={false}
            className="text-sm whitespace-pre-wrap break-words"
            data-testid="chat-message-text"
          />
        ) : (
          <div
            className="text-sm whitespace-pre-wrap break-words"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(getProcessedContent(rawContent)) }}
            data-testid="chat-message-text"
          />
        )}
        {!isUser && (message as Message).keyInsights && (message as Message).keyInsights!.length > 0 && (
          <KeyInsightsCard insights={(message as Message).keyInsights!} />
        )}
        {!isUser && (message as Message).recommendedActions && (message as Message).recommendedActions!.length > 0 && (
          <RecommendedActionsCard actions={(message as Message).recommendedActions!} />
        )}
        {!isUser && details && details.length > 0 && (
          <DetailsTable details={details} />
        )}
        <div
          className={`text-xs mt-1 flex items-center gap-1 ${
            isUser ? 'text-blue-100' : 'text-gray-500'
          }`}
          data-testid="chat-message-timestamp"
        >
          {message?.timestamp?.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })}
          {!isUser && (message as Message).metadata && (
            <CostTooltip metadata={(message as Message).metadata!} />
          )}
          {!isUser && (message as Message).confidenceScore != null && (
            <ConfidenceBadge
              score={(message as Message).confidenceScore!}
              uncertaintyReasons={(message as Message).uncertaintyReasons}
            />
          )}
          {!isUser && (message as Message).tokenUsage && (
            <ContextUsageBadge tokenUsage={(message as Message).tokenUsage!} />
          )}
        </div>
      </div>
    </div>
  );
}
